import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, ElementRef, inject, input, signal, untracked, viewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { renderAsync } from 'docx-preview';

import { LangPipe, LangService } from '@reforgium/presentia';
import { SkButtonComponent, SkDialogComponent } from '@ss/ui';

import { DocumentFileDto } from 'entities/document.model';
import { DocumentService } from 'use-cases/document.service';
import { AccessibilityService } from 'features/accessibility/accessibility.service';

import { PdfTextPreviewComponent } from './pdf-text-preview.component';

type ParagraphAlignment = 'left' | 'center' | 'right' | 'justify';
type DocumentCell = { text: string; alignment: ParagraphAlignment };
type DocumentBlock =
  | { type: 'paragraph'; text: string; alignment: ParagraphAlignment }
  | { type: 'table'; rows: DocumentCell[][][] };

@Component({
  selector: 'app-document-file-preview',
  template: `
    @if (canPreview()) {
      @if (inDialog()) {
        <div class="preview-trigger">
          <sk-button
            type="button"
            icon="eye"
            appearance="secondary"
            [label]="'document.actions.preview-file' | lang"
            (clicked)="dialogVisible.set(true)"
          />
        </div>

        <sk-dialog
          [(visible)]="dialogVisible"
          [header]="filename()"
          [ariaLabel]="'document.actions.preview-file' | lang"
          [dismissOnBackdrop]="true"
          styleClass="document-preview-dialog"
        >
          <ng-container [ngTemplateOutlet]="previewContent" />
        </sk-dialog>
      } @else {
        <ng-container [ngTemplateOutlet]="previewContent" />
      }
    }

    <ng-template #previewContent>
      <section
        class="file-preview"
        [class.file-preview--dialog]="inDialog()"
        [attr.aria-label]="'document.actions.preview-file' | lang"
        [attr.lang]="
          storedFile()?.language === 'KY' ? 'ky' : storedFile()?.language === 'RU' ? 'ru' : language.currentLang()
        "
      >
        <div class="file-preview__content">
          <div #docxContainer class="docx-library-preview" [class.docx-library-preview--visible]="docxPreview()"></div>

          @if (textPreview()) {
            <pre class="text-preview">{{ textContent() }}</pre>
          } @else if (loading()) {
            <p class="state">Загрузка предпросмотра…</p>
          } @else if (error()) {
            <p class="state">Не удалось открыть файл для предпросмотра.</p>
          } @else if (pdfData(); as pdf) {
            @if (accessiblePdf()) {
              <div class="pdf-mode">
                <button type="button" (click)="togglePdfMode()">
                  {{
                    (showOriginalPdf()
                      ? 'common.accessibility.pdfTextVersion'
                      : 'common.accessibility.pdfOriginalVersion'
                    ) | lang
                  }}
                </button>
                @if (showOriginalPdf()) {
                  <iframe class="pdf-preview" [src]="safePdfUrl()" [title]="filename()"></iframe>
                } @else {
                  <app-pdf-text-preview
                    [data]="pdf"
                    [language]="
                      storedFile()?.language === 'KY'
                        ? 'ky'
                        : storedFile()?.language === 'RU'
                          ? 'ru'
                          : language.currentLang()
                    "
                  />
                }
              </div>
            } @else {
              <iframe class="pdf-preview" [src]="safePdfUrl()" [title]="filename()"></iframe>
            }
          } @else if (!docxPreview()) {
            <article class="docx-preview">
              @for (block of documentBlocks(); track $index) {
                @if (block.type === 'paragraph') {
                  <p [class]="'align-' + block.alignment">{{ block.text }}</p>
                } @else {
                  <table>
                    @for (row of block.rows; track $index) {
                      <tr>
                        @for (cell of row; track $index) {
                          <td>
                            @for (paragraph of cell; track $index) {
                              <p [class]="'align-' + paragraph.alignment">{{ paragraph.text }}</p>
                            }
                          </td>
                        }
                      </tr>
                    }
                  </table>
                }
              } @empty {
                <p class="state">В документе нет текста для отображения.</p>
              }
            </article>
          }
        </div>
      </section>
    </ng-template>
  `,
  styles: `
    :host {
      --sk-overlay-dialog-width: min(92vw, 1100px);
      display: block;
      width: 100%;
      min-width: 0;
    }

    .preview-trigger {
      display: flex;
      justify-content: flex-end;
      margin: 0;
    }

    .file-preview {
      display: flex;
      flex-direction: column;
      width: 100%;
      min-width: 0;
      overflow: hidden;
    }

    .file-preview--dialog .file-preview__content {
      height: min(76dvh, 850px);
      min-height: min(520px, 70dvh);
    }

    .file-preview__content {
      position: relative;
      display: flex;
      flex-direction: column;
      width: 100%;
      min-width: 0;
      min-height: 420px;
      height: clamp(420px, 70dvh, 850px);
      overflow: hidden;
    }

    .pdf-mode {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-height: 0;
    }

    .pdf-mode > button {
      align-self: flex-end;
      margin: 0.4rem;
      padding: 0.4rem 0.7rem;
      border: 1px solid var(--border-color);
      border-radius: 0.5rem;
      background: var(--surface-neutral);
      color: var(--text-primary);
      font: inherit;
      font-size: 0.8rem;
      cursor: pointer;
    }

    .pdf-mode > button:focus-visible {
      outline: 3px solid var(--primary-color);
      outline-offset: 2px;
    }

    .pdf-preview {
      display: block;
      flex: 1 1 auto;
      width: 100%;
      height: 100%;
      min-width: 0;
      min-height: 0;
      border: 0;
    }

    .docx-library-preview {
      display: none;
      flex: 1 1 auto;
      width: 100%;
      height: 100%;
      min-width: 0;
      min-height: 0;
      overflow: auto;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
    }

    .docx-library-preview--visible {
      display: block;
    }

    :host ::ng-deep .docx-library-preview .docx-wrapper {
      box-sizing: border-box;
      min-width: max-content;
      min-height: 100%;
      padding: 16px !important;
      background: transparent !important;
    }

    :host ::ng-deep .docx-library-preview section.docx {
      flex: 0 0 auto;
      margin: 0 auto !important;
      border: 0 !important;
      box-shadow: none !important;
    }

    .text-preview {
      flex: 1 1 auto;
      width: 100%;
      min-width: 0;
      min-height: 0;
      overflow: auto;
      box-sizing: border-box;
      margin: 0;
      padding: 2rem;
      color: var(--text-primary);
      background: transparent;
      font: inherit;
      line-height: 1.55;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }

    .docx-preview {
      flex: 1 1 auto;
      width: 100%;
      min-width: 0;
      min-height: 0;
      overflow: auto;
      box-sizing: border-box;
      padding: 2rem;
      color: var(--text-primary);
      line-height: 1.55;
    }

    .docx-preview p {
      margin: 0 0 0.75rem;
      white-space: pre-wrap;
    }

    .docx-preview p:last-child {
      margin-bottom: 0;
    }

    .docx-preview .align-left {
      text-align: left;
    }

    .docx-preview .align-center {
      text-align: center;
    }

    .docx-preview .align-right {
      text-align: right;
    }

    .docx-preview .align-justify {
      text-align: justify;
    }

    .docx-preview table {
      width: 100%;
      margin: 1rem 0;
      border-collapse: collapse;
    }

    .docx-preview td {
      padding: 0.5rem;
      border: 1px solid var(--border-color, #ddd);
      vertical-align: top;
      white-space: pre-wrap;
    }

    .state {
      display: flex;
      flex: 1 1 auto;
      align-items: center;
      justify-content: center;
      min-height: 0;
      margin: 0;
      padding: 2rem;
      color: var(--text-secondary);
    }
  `,
  imports: [LangPipe, NgTemplateOutlet, SkButtonComponent, SkDialogComponent, PdfTextPreviewComponent],
})
export class DocumentFilePreviewComponent {
  file = input<File | null>(null);
  storedFile = input<DocumentFileDto | null>(null);
  versionId = input<number | undefined>();
  inDialog = input(false);
  speechPreview = input(false);

  protected dialogVisible = signal(false);

  private readonly documentService = inject(DocumentService);
  private readonly sanitizer = inject(DomSanitizer);
  protected readonly accessibility = inject(AccessibilityService);
  protected readonly accessiblePdf = computed(() => {
    const settings = this.accessibility.settings();

    return (
      this.speechPreview() &&
      (settings.soundAssistant ||
        settings.fontSize !== 'normal' ||
        settings.fontFamily !== 'default' ||
        settings.letterSpacing !== 'normal' ||
        settings.colorScheme !== 'default')
    );
  });
  protected readonly language = inject(LangService);

  protected readonly loading = signal(false);
  protected readonly error = signal(false);
  protected readonly pdfUrl = signal<string | null>(null);
  protected readonly pdfData = signal<ArrayBuffer | null>(null);
  protected readonly showOriginalPdf = signal(false);

  protected readonly documentBlocks = signal<DocumentBlock[]>([]);
  protected readonly textPreview = signal(false);
  protected readonly textContent = signal('');
  protected readonly safePdfUrl = signal<SafeResourceUrl | null>(null);
  protected readonly filename = computed(() => this.file()?.name ?? this.storedFile()?.originalFileName ?? '');
  protected readonly canPreview = computed(() => {
    const name = this.filename().toLowerCase();

    return (
      name.endsWith('.pdf') ||
      name.endsWith('.docx') ||
      name.endsWith('.txt') ||
      name.endsWith('.md') ||
      this.storedFile()?.format === 'PDF' ||
      this.storedFile()?.format === 'DOCX' ||
      this.storedFile()?.format === 'TXT'
    );
  });
  protected readonly docxPreview = signal(false);
  private readonly docxContainer = viewChild<ElementRef<HTMLElement>>('docxContainer');

  constructor() {
    effect(() => {
      this.file();
      this.storedFile();
      untracked(() => this.reset());
    });

    effect(() => {
      const file = this.file();
      const storedFile = this.storedFile();
      const versionId = this.versionId();
      const shouldRender = !this.inDialog() || this.dialogVisible();

      if (shouldRender && (file || (storedFile && versionId))) {
        void this.open();
      }
    });
  }

  async open() {
    this.loading.set(true);
    this.error.set(false);
    this.docxPreview.set(false);
    this.textPreview.set(false);
    this.textContent.set('');

    let previewData: { data: ArrayBuffer | string; format: 'PDF' | 'DOCX' | 'TXT' } | null = null;

    try {
      previewData = await this.loadPreviewData();
      const { data, format } = previewData;

      if (format === 'TXT') {
        this.textContent.set(typeof data === 'string' ? data : await new Blob([data]).text());
        this.textPreview.set(true);

        return;
      }

      if (format === 'DOCX' && data instanceof ArrayBuffer) {
        this.docxPreview.set(true);
        await new Promise((resolve) => setTimeout(resolve, 0));
        const container = this.docxContainer()?.nativeElement;

        if (!container) {
          throw new Error('DOCX preview container is unavailable');
        }

        await renderAsync(data, container, container, {
          inWrapper: true,
          breakPages: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreLastRenderedPageBreak: false,
        });

        return;
      }

      if (format === 'PDF') {
        const bytes = data instanceof ArrayBuffer ? data : await new Blob([data]).arrayBuffer();
        const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));

        this.pdfData.set(bytes);
        this.pdfUrl.set(url);
        this.safePdfUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url + '#view=FitH&navpanes=0'));
      } else {
        this.documentBlocks.set(this.parseDocumentXml(typeof data === 'string' ? data : await new Blob([data]).text()));
      }
    } catch {
      this.docxPreview.set(false);
      this.textPreview.set(false);

      if (previewData?.format === 'DOCX' && previewData?.data instanceof ArrayBuffer) {
        try {
          const xml = await this.extractDocxXml(previewData?.data);

          this.documentBlocks.set(this.parseDocumentXml(xml));

          return;
        } catch {
          // The visible error state below handles invalid or unsupported files.
        }
      }

      this.error.set(true);
    } finally {
      this.loading.set(false);
    }
  }
  protected togglePdfMode(): void {
    this.showOriginalPdf.update((shown) => !shown);
  }

  private reset() {
    const url = this.pdfUrl();

    url && URL.revokeObjectURL(url);
    this.pdfUrl.set(null);
    this.pdfData.set(null);
    this.showOriginalPdf.set(false);
    this.safePdfUrl.set(null);
    this.docxPreview.set(false);
    this.textPreview.set(false);
    this.textContent.set('');
    this.documentBlocks.set([]);
    this.error.set(false);
  }

  private async loadPreviewData(): Promise<{ data: ArrayBuffer | string; format: 'PDF' | 'DOCX' | 'TXT' }> {
    const localFile = this.file();

    this.safePdfUrl.set(null);

    if (localFile) {
      const filename = localFile.name.toLowerCase();
      const format = filename.endsWith('.pdf')
        ? 'PDF'
        : filename.endsWith('.txt') || filename.endsWith('.md')
          ? 'TXT'
          : 'DOCX';

      return { data: await localFile.arrayBuffer(), format };
    }

    const storedFile = this.storedFile();
    const versionId = this.versionId();

    if (!storedFile || !versionId) throw new Error('File metadata is unavailable');
    const blob = await this.documentService.download(versionId, storedFile.id);

    if (storedFile.format === 'PDF') return { data: await blob.arrayBuffer(), format: 'PDF' };

    if (storedFile.format === 'TXT') return { data: await blob.text(), format: 'TXT' };

    const data = await blob.arrayBuffer();
    const signature = new Uint8Array(data.slice(0, 2));

    return signature[0] === 0x50 && signature[1] === 0x4b
      ? { data, format: 'DOCX' }
      : { data: new TextDecoder().decode(data), format: 'DOCX' };
  }

  private parseDocumentXml(xml: string): DocumentBlock[] {
    const parsed = new DOMParser().parseFromString(xml, 'application/xml');

    if (parsed.querySelector('parsererror')) throw new Error('Invalid DOCX XML');
    const body = Array.from(parsed.getElementsByTagNameNS('*', 'body'))[0];

    if (!body) return [];

    return Array.from(body.children).flatMap((node): DocumentBlock[] => {
      if (node.localName === 'p') return [{ type: 'paragraph', ...this.parseCell(node) }];
      if (node.localName !== 'tbl') return [];
      const rows = Array.from(node.children)
        .filter((row) => row.localName === 'tr')
        .map((row) =>
          Array.from(row.children)
            .filter((cell) => cell.localName === 'tc')
            .map((cell) =>
              Array.from(cell.children)
                .filter((paragraph) => paragraph.localName === 'p')
                .map((paragraph) => this.parseCell(paragraph)),
            ),
        );

      return [{ type: 'table', rows }];
    });
  }

  private parseCell(node: Element): DocumentCell {
    const properties = Array.from(node.children).find((child) => child.localName === 'pPr');
    const value =
      properties &&
      Array.from(properties.children)
        .find((child) => child.localName === 'jc')
        ?.getAttributeNS('*', 'val');

    return { text: this.nodeText(node), alignment: this.toAlignment(value) };
  }

  private nodeText(node: Element): string {
    return Array.from(node.getElementsByTagNameNS('*', '*')).reduce((text, child) => {
      if (child.localName === 't') return text + (child.textContent ?? '');
      if (child.localName === 'tab') return text + '\t';

      return child.localName === 'br' || child.localName === 'cr' ? text + '\n' : text;
    }, '');
  }

  private toAlignment(value: string | null | undefined): ParagraphAlignment {
    if (value === 'center' || value === 'right') return value;
    if (value === 'both' || value === 'distribute') return 'justify';

    return 'left';
  }

  private async extractDocxXml(buffer: ArrayBuffer): Promise<string> {
    const bytes = new Uint8Array(buffer);
    const view = new DataView(buffer);

    for (let offset = bytes.length - 22; offset >= Math.max(0, bytes.length - 65557); offset--) {
      if (view.getUint32(offset, true) !== 0x06054b50) continue;
      let entryOffset = view.getUint32(offset + 16, true);
      const entries = view.getUint16(offset + 10, true);

      for (let index = 0; index < entries; index++) {
        if (view.getUint32(entryOffset, true) !== 0x02014b50) break;
        const compression = view.getUint16(entryOffset + 10, true);
        const compressedSize = view.getUint32(entryOffset + 20, true);
        const nameLength = view.getUint16(entryOffset + 28, true);
        const extraLength = view.getUint16(entryOffset + 30, true);
        const commentLength = view.getUint16(entryOffset + 32, true);
        const localOffset = view.getUint32(entryOffset + 42, true);
        const name = new TextDecoder().decode(bytes.slice(entryOffset + 46, entryOffset + 46 + nameLength));

        if (name === 'word/document.xml') {
          const localNameLength = view.getUint16(localOffset + 26, true);
          const localExtraLength = view.getUint16(localOffset + 28, true);
          const compressed = bytes.slice(
            localOffset + 30 + localNameLength + localExtraLength,
            localOffset + 30 + localNameLength + localExtraLength + compressedSize,
          );

          if (compression === 0) return new TextDecoder().decode(compressed);
          if (compression === 8)
            return new Response(
              new Blob([compressed]).stream().pipeThrough(new DecompressionStream('deflate-raw')),
            ).text();
          throw new Error('Unsupported DOCX compression');
        }

        entryOffset += 46 + nameLength + extraLength + commentLength;
      }
    }

    throw new Error('DOCX document XML was not found');
  }
}
